import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Team, TeamMember, TeamMemberActivity } from '../../models/types';
import { apiService } from '../../services/api.service';
import { groupService } from '../../services/group.service';
import { BreadcrumbTrail } from '../ui/BreadcrumbTrail';
import { PageHeader } from '../ui/PageHeader';
import { StatusPill } from '../ui/StatusPill';
import { EmptyState } from '../ui/EmptyState';
import ActivityDetailsModal from '../contests/ActivityDetailsModal';
import { formatShortDate } from '../contests/contestFormat';
import { TeamDetailStatsGrid, TeamStatsData } from './TeamDetailStatsGrid';
import { TeamDetailMembersCard } from './TeamDetailMembersCard';
import { TeamRemoveMemberDialog } from './TeamRemoveMemberDialog';
import { getContestStatus, getMemberDisplayName } from './teamDetailFormatters';
import { useTeamMemberContestStats } from './useTeamMemberContestStats';

const EMPTY_STATS: TeamStatsData = { totalDistance: 0, averagePace: 0, totalTracklog: 0, fastestPace: 0, maxDistance: 0 };

const fetchMemberActivities = async (teamId: string, memberId: string): Promise<TeamMemberActivity[]> =>
  (await apiService.getTeamMemberActivities(teamId, memberId)).map(activity => ({
    ...activity,
    startDate: new Date(activity.startDate),
    createdAt: new Date(activity.createdAt),
    updatedAt: new Date(activity.updatedAt)
  }));

const toTeam = (data: Team): Team => ({ ...data, createdAt: new Date(data.createdAt), updatedAt: new Date(data.updatedAt) });

export const TeamDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('teams');
  const [team, setTeam] = useState<Team | null>(null);
  const [contest, setContest] = useState<any | null>(null);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const [memberActivities, setMemberActivities] = useState<TeamMemberActivity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [teamStats, setTeamStats] = useState<TeamStatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userGroupRole, setUserGroupRole] = useState<'admin' | 'member' | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);
  const [deletingMember, setDeletingMember] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });
  const memberStats = useTeamMemberContestStats(team?.contestId, id);

  const fetchTeamStats = async (teamId: string) => {
    try {
      setStatsLoading(true);
      const res = await apiService.getTeamStats(teamId);
      setTeamStats({
        totalDistance: res.stats.totalDistance,
        averagePace: res.stats.averagePace,
        totalTracklog: res.stats.totalTracklog,
        fastestPace: res.stats.fastestPace,
        maxDistance: res.stats.maxDistance
      });
    } catch (error) {
      console.error('Error fetching team stats:', error);
      setTeamStats(EMPTY_STATS);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    const fetchTeamDetail = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const teamData = await apiService.getTeamDetail(id);
        setTeam(toTeam(teamData));

        await fetchTeamStats(id);

        if (teamData.contestId) {
          try {
            const contestData = await apiService.getContest(teamData.contestId);
            setContest({
              ...contestData,
              startAt: new Date(contestData.startAt),
              endAt: new Date(contestData.endAt)
            });

            // Only feeds the breadcrumb, so a failure just drops that crumb.
            groupService
              .getGroup(contestData.groupId)
              .then(res => setGroupName(res.data?.name ?? null))
              .catch(groupError => console.error('Error fetching group name:', groupError));

            try {
              const roleData = await apiService.getUserGroupRoleForContest(teamData.contestId);
              setUserGroupRole(roleData.userRole);
            } catch (roleError) {
              console.error('Error fetching user group role:', roleError);
              setUserGroupRole(null);
            }
          } catch (contestError) {
            console.error('Error fetching contest data:', contestError);
          }
        }
      } catch (error) {
        console.error('Error fetching team detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamDetail();
  }, [id]);

  // Only used to highlight the viewer's own row, so a failure is non-fatal.
  useEffect(() => {
    apiService
      .getUserProfile()
      .then(profile => setCurrentUserId(profile._id))
      .catch(error => console.error('Error fetching user profile:', error));
  }, []);

  // The drawer opens before the request resolves, so a slow response for a previously
  // clicked member must not overwrite the activities of the member now shown.
  const activeMemberRequest = useRef<string | null>(null);

  const handleMemberClick = async (memberId: string) => {
    if (!team) return;
    activeMemberRequest.current = memberId;

    try {
      setActivitiesLoading(true);
      setSelectedMember(memberId);
      setMemberActivities([]);
      setDrawerOpen(true);
      const activities = await fetchMemberActivities(team._id, memberId);
      if (activeMemberRequest.current !== memberId) return;
      setMemberActivities(activities);
    } catch (error) {
      console.error('Error fetching member activities:', error);
      if (activeMemberRequest.current !== memberId) return;
      setDrawerOpen(false);
      setSelectedMember(null);
    } finally {
      if (activeMemberRequest.current === memberId) setActivitiesLoading(false);
    }
  };

  // Admin review of a flagged activity; refetch because a Strava edit rebuilds records under new ids.
  const handleReviewSuspicious = async (activityId: string, dismissed: boolean) => {
    if (!team || !selectedMember) return;
    const memberId = selectedMember;
    await apiService.reviewSuspiciousActivity(team.contestId, activityId, dismissed);
    const [activities] = await Promise.all([fetchMemberActivities(team._id, memberId), memberStats.reload()]);
    if (activeMemberRequest.current === memberId) setMemberActivities(activities);
  };

  const handleCloseDrawer = () => {
    activeMemberRequest.current = null;
    setDrawerOpen(false);
    setSelectedMember(null);
    setMemberActivities([]);
  };

  const getSelectedMemberName = (): string => {
    const member = team?.members.find(m => m.userId === selectedMember);
    return member ? getMemberDisplayName(member) : '';
  };

  const confirmRemoveMember = async () => {
    if (!memberToDelete || !team) return;

    try {
      setDeletingMember(true);
      await apiService.removeMemberFromTeam(team._id, memberToDelete.userId);

      setTeam(toTeam(await apiService.getTeamDetail(team._id)));
      await Promise.all([fetchTeamStats(team._id), memberStats.reload()]);

      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: t('detail.removeSuccess', { name: getMemberDisplayName(memberToDelete) }),
        severity: 'success'
      });
      setMemberToDelete(null);
    } catch (error) {
      console.error('Error removing team member:', error);
      setSnackbar({ open: true, message: t('detail.removeFailed'), severity: 'error' });
    } finally {
      setDeletingMember(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!team) {
    return (
      <EmptyState
        title={t('detail.notFoundTitle')}
        description={t('detail.notFoundDescription')}
        action={
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            {t('detail.goBack')}
          </Button>
        }
      />
    );
  }

  const status = contest ? getContestStatus(contest.startAt, contest.endAt) : null;
  // Rosters freeze once the contest ends, matching the contest page.
  const membersLocked = !!contest && contest.endAt <= new Date();
  // Same trail as the contest page: teams live under a contest, which lives under a group.
  const crumbs = [
    { label: t('detail.breadcrumbGroups'), to: '/groups' },
    ...(contest && groupName ? [{ label: groupName, to: `/groups/${contest.groupId}` }] : []),
    ...(contest ? [{ label: contest.name, to: `/contests/${team.contestId}` }] : []),
    { label: team.name },
  ];

  return (
    <>
      <BreadcrumbTrail items={crumbs} />

      <PageHeader
        eyebrow={status && <StatusPill label={status.label} tone={status.tone} dot />}
        title={team.name}
        subtitle={contest && `${contest.name} · ${formatShortDate(contest.startAt)} – ${formatShortDate(contest.endAt)}`}
        actions={
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            {t('detail.backToContest')}
          </Button>
        }
      />

      <TeamDetailStatsGrid team={team} stats={teamStats} loading={statsLoading} />

      <TeamDetailMembersCard
        members={team.members}
        currentUserId={currentUserId}
        statsByUser={memberStats.statsByUser}
        statsLoading={memberStats.loading}
        canManageMembers={userGroupRole === 'admin' && !membersLocked}
        onOpenMember={handleMemberClick}
        onRemoveMember={member => {
          setMemberToDelete(member);
          setDeleteDialogOpen(true);
        }}
      />

      <ActivityDetailsModal
        open={drawerOpen}
        onClose={handleCloseDrawer}
        userName={getSelectedMemberName()}
        teamName={contest ? `${team.name} · ${contest.name}` : team.name}
        activities={memberActivities.map(a => ({
          activityId: a._id,
          distance: a.distance,
          pace: a.pace,
          movingTime: a.movingTime,
          workoutType: a.workoutType,
          startDate: a.startDate.toISOString(),
          stravaActivityId: a.stravaActivityId,
          suspicious: a.suspicious,
          suspiciousReasons: a.suspiciousReasons,
          suspiciousDismissed: a.suspiciousDismissed,
          splits: a.splits
        }))}
        loading={activitiesLoading}
        onReviewSuspicious={handleReviewSuspicious}
      />

      <TeamRemoveMemberDialog
        open={deleteDialogOpen}
        memberName={memberToDelete ? getMemberDisplayName(memberToDelete) : ''}
        deleting={deletingMember}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={confirmRemoveMember}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
